import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { FolderOpen, Search, User, ChevronRight, Loader2, Bed } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { useOssAuth } from "../../context/OssAuthContext";
import OssSectionPageHeader from "../../components/oss/OssSectionPageHeader";
import OssTerapiaAlertBadge from "../../components/oss/OssTerapiaAlertBadge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDataNascita(s) {
  if (!s || !String(s).trim()) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleDateString("it-IT");
}

/** Mostra "Camera …" se il testo non contiene già "camera" (qualsiasi maiuscola). */
function labelCamera(camera) {
  const s = (camera ?? "").trim();
  if (!s || s === "N/A") return s;
  if (/camera/i.test(s)) return s;
  return `Camera ${s}`;
}

/** Stili avatar in base al sesso anagrafico (M → blu, F → rosa). */
function getSessoAvatarStyles(sesso) {
  const raw = (sesso ?? "").toString().trim();
  const u = raw.toUpperCase();
  const lower = raw.toLowerCase();
  const femmina =
    u === "F" || u === "FEMMINA" || lower === "femmina";
  const maschio =
    u === "M" || u === "MASCHIO" || lower === "maschio";
  if (femmina) {
    return {
      fotoRing: "ring-2 ring-pink-400 ring-offset-2 border-transparent",
      placeholderBg:
        "bg-gradient-to-br from-pink-50 to-rose-100 border-pink-200/90",
      iconClass: "text-pink-600",
    };
  }
  if (maschio) {
    return {
      fotoRing: "ring-2 ring-blue-500 ring-offset-2 border-transparent",
      placeholderBg:
        "bg-gradient-to-br from-sky-50 to-blue-100 border-blue-200/90",
      iconClass: "text-blue-600",
    };
  }
  return {
    fotoRing: "border border-slate-200",
    placeholderBg: "bg-gradient-to-br from-slate-100 to-slate-200 border-slate-200/80",
    iconClass: "text-slate-500",
  };
}

export default function OssFascicoli() {
  const { token } = useOssAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const detailBase = location.pathname.startsWith("/sanitario")
    ? "/sanitario/scheda"
    : "/oss/fascicoli";
  const [fascicoli, setFascicoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const searchInputRef = useRef(null);
  const [searchApplied, setSearchApplied] = useState("");

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/oss/me/fascicoli`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setFascicoli(data || []))
      .catch(() => setFascicoli([]))
      .finally(() => setLoading(false));
  }, [token]);

  const applySearch = () => {
    setSearchApplied((searchInputRef.current?.value ?? "").trim().toLowerCase());
  };

  const filtered = fascicoli.filter((f) => {
    const q = searchApplied;
    return (
      !q ||
      (f.nome_completo || "").toLowerCase().includes(q) ||
      (f.camera || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="max-w-6xl mx-auto">
      <OssSectionPageHeader
        sectionKey="fascicoli"
        title="Fascicoli Assistenziali"
        subtitle={
          !loading && fascicoli.length > 0 ? (
            <span className="tabular-nums">
              {filtered.length === fascicoli.length ? (
                <span>{fascicoli.length} ospit{fascicoli.length === 1 ? "e" : "i"}</span>
              ) : (
                <span>
                  {filtered.length} di {fascicoli.length} risultat{filtered.length === 1 ? "o" : "i"}
                </span>
              )}
            </span>
          ) : null
        }
        toolbar={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                ref={searchInputRef}
                placeholder="Cerca per nome o camera…"
                defaultValue=""
                onBlur={applySearch}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    applySearch();
                  }
                }}
                className="pl-9 h-11 w-full bg-white/80"
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              className="shrink-0 sm:w-auto w-full border-slate-200 min-h-[44px]"
              onClick={applySearch}
            >
              Applica filtro
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>{searchApplied ? "Nessun risultato per la ricerca" : "Nessun fascicolo trovato"}</p>
        </div>
      ) : (
        <ul className="space-y-2.5" role="list">
          {filtered.map((f) => {
            const av = getSessoAvatarStyles(f.sesso);
            return (
            <li key={f.id}>
              <Card
                className="group cursor-pointer hover:shadow-md transition-all border border-slate-200/90 hover:border-purple-400/70 bg-white hover:bg-purple-50/40"
                onClick={() => navigate(`${detailBase}/${f.id}`)}
              >
                <CardContent className="p-0">
                  {/* Phone e tablet: colonna con diagnosi sempre visibile (evita griglia che schiaccia la colonna 1fr) */}
                  <div className="flex items-stretch gap-3 px-4 py-3.5 sm:px-6 sm:py-4 lg:hidden">
                    {f.foto_url ? (
                      <img
                        src={f.foto_url}
                        alt=""
                        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover shrink-0 self-start ${av.fotoRing}`}
                      />
                    ) : (
                      <div
                        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shrink-0 self-start border ${av.placeholderBg}`}
                      >
                        <User className={`w-6 h-6 sm:w-7 sm:h-7 ${av.iconClass}`} />
                      </div>
                    )}
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                      <p className="font-semibold text-slate-900 text-base leading-tight">
                        {f.nome_completo}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <OssTerapiaAlertBadge
                          count={f.alert_terapie ?? f.alert_terapie_extra}
                          overdue={f.alert_terapie_overdue}
                        />
                        <span className="text-xs text-slate-500 tabular-nums">
                          {formatDataNascita(f.data_nascita)}
                        </span>
                        {f.camera && f.camera !== "N/A" && (
                          <Badge variant="outline" className="text-[11px] px-1.5 py-0 h-5 font-normal">
                            <Bed className="w-3 h-3 mr-0.5" />
                            {labelCamera(f.camera)}
                          </Badge>
                        )}
                      </div>
                      <div className="min-w-0 border-t border-slate-100 pt-2">
                        {f.diagnosi_principale ? (
                          <p className="text-xs sm:text-sm text-slate-600 leading-snug sm:line-clamp-4 line-clamp-3">
                            <span className="text-[10px] uppercase tracking-wide text-slate-400 block mb-0.5">
                              Diagnosi principale
                            </span>
                            {f.diagnosi_principale}
                          </p>
                        ) : (
                          <p className="text-xs text-slate-400 italic">Nessuna diagnosi principale</p>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-start pt-1">
                      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-purple-500" />
                    </div>
                  </div>

                  {/* Desktop largo (lg+): griglia allineata; sotto lg lo spazio orizzontale non basta per 6 colonne fisse */}
                  <div className="hidden lg:grid lg:grid-cols-[3.5rem_14rem_7.5rem_10rem_minmax(0,1fr)_auto] lg:items-center lg:gap-x-4 lg:px-5 lg:py-4">
                    {f.foto_url ? (
                      <img
                        src={f.foto_url}
                        alt=""
                        className={`w-14 h-14 rounded-full object-cover justify-self-start ${av.fotoRing}`}
                      />
                    ) : (
                      <div
                        className={`w-14 h-14 rounded-full flex items-center justify-center border justify-self-start ${av.placeholderBg}`}
                      >
                        <User className={`w-7 h-7 ${av.iconClass}`} />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-base leading-tight truncate" title={f.nome_completo}>
                        {f.nome_completo}
                      </p>
                      <div className="mt-1">
                        <OssTerapiaAlertBadge
                          count={f.alert_terapie ?? f.alert_terapie_extra}
                          overdue={f.alert_terapie_overdue}
                          label="da somministrare"
                        />
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 tabular-nums leading-snug">
                      <span className="text-[10px] uppercase tracking-wide text-slate-400 block">Nascita</span>
                      {formatDataNascita(f.data_nascita)}
                    </div>
                    <div className="flex min-h-[2.25rem] items-center">
                      {f.camera && f.camera !== "N/A" ? (
                        <Badge
                          variant="outline"
                          className="text-xs font-normal max-w-full justify-center py-1 border-slate-200 bg-slate-50/80"
                        >
                          <Bed className="w-3.5 h-3.5 mr-1 shrink-0 text-slate-500" />
                          <span className="truncate">{labelCamera(f.camera)}</span>
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </div>
                    <div className="min-w-0 border-l border-slate-100 pl-4">
                      {f.diagnosi_principale ? (
                        <p className="text-sm text-slate-600 leading-snug line-clamp-2">
                          <span className="text-slate-400 mr-1.5">Diagnosi:</span>
                          {f.diagnosi_principale}
                        </p>
                      ) : (
                        <p className="text-xs text-slate-400 italic">Nessuna diagnosi principale</p>
                      )}
                    </div>
                    <div className="flex items-center justify-end pl-2 border-l border-slate-100">
                      <ChevronRight className="w-6 h-6 text-slate-300 group-hover:text-purple-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
